import { useState, useCallback } from 'react';
import { RefreshCw, Loader2, Copy, Check, Save } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const AI_TARGETS = ['chatgpt', 'claude', 'gemini', 'perplexity', 'copilot'];

export default function PILoopPage() {
  const [outcome, setOutcome] = useState('');
  const [context, setContext] = useState('');
  const [targetAi, setTargetAi] = useState('chatgpt');
  const [maxIter, setMaxIter] = useState(3);
  const [threshold, setThreshold] = useState(0.8);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleLoop = useCallback(async () => {
    if (outcome.trim().length < 5 || loading) return;
    setLoading(true);
    setResult(null);
    try {
      const res = await fetch(`${API}/api/ai/loop`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ outcome, target_ai: targetAi, max_iterations: maxIter, threshold, context }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Failed'); }
      setResult(await res.json());
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  }, [outcome, targetAi, maxIter, threshold, context, loading]);

  const handleCopy = () => {
    if (result?.final_cast) {
      navigator.clipboard.writeText(result.final_cast);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleSave = async () => {
    if (!result?.final_cast || saving) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/ai/my-casts`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cast: result.final_cast, title: outcome.slice(0, 60), target_ai: targetAi, source: 'pi_loop' }),
      });
      if (res.ok) toast.success('Cast saved to your library!');
      else throw new Error('Failed to save');
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="loop-page">
      <div>
        <h1 className="font-space text-2xl md:text-3xl text-white mb-2">PI Loop</h1>
        <p className="text-white/40 text-sm">Automated outcome refinement &mdash; generates, evaluates, and refines until your threshold is met</p>
      </div>

      {/* How to use */}
      <div className="bg-[#0E1830] border border-white/5 rounded-xl p-4">
        <details>
          <summary className="text-white/50 text-xs font-bold uppercase tracking-wider cursor-pointer hover:text-white/70">How to use PI Loop</summary>
          <div className="mt-3 text-white/35 text-xs space-y-1 leading-relaxed">
            <p>1. State your desired outcome clearly</p>
            <p>2. Set the max number of refinement iterations (1&ndash;5)</p>
            <p>3. Set the quality threshold (higher = more refinements needed)</p>
            <p>4. Click &ldquo;Start PI Loop&rdquo; &mdash; it will generate, evaluate, and refine automatically</p>
            <p>5. Watch the iteration progress bars as it improves the cast</p>
            <p>6. Copy or Save the final cast when satisfied</p>
          </div>
        </details>
      </div>

      {/* Samples */}
      <div className="flex flex-wrap gap-2">
        <span className="text-white/20 text-xs self-center">Try:</span>
        {[
          { label: 'Cold email', outcome: 'Write a cold email that gets a 30% reply rate from CTOs' },
          { label: 'Technical doc', outcome: 'Generate API documentation that developers rate 9/10 for clarity' },
          { label: 'Sales pitch', outcome: 'Create a 60-second elevator pitch for a fintech startup' },
        ].map(s => (
          <button key={s.label} onClick={() => { setOutcome(s.outcome); setResult(null); }}
            className="px-3 py-1 text-xs rounded-full border border-white/10 text-white/40 hover:text-white/70 hover:border-white/20 transition-all">{s.label}</button>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Input */}
        <div className="bg-[#0E1830] border border-white/5 rounded-2xl p-6 space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/30 font-bold block mb-2">Desired outcome</label>
            <textarea value={outcome} onChange={e => setOutcome(e.target.value)}
              placeholder="e.g. Write a cold email that gets a 30% reply rate from CTOs..."
              className="w-full h-24 bg-white/[0.04] border border-white/[0.08] rounded-xl p-4 text-white/90 text-sm resize-none focus:border-[#7C3AED]/40 focus:outline-none placeholder:text-white/20"
              data-testid="loop-outcome" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/30 font-bold block mb-2">Context (optional)</label>
            <input value={context} onChange={e => setContext(e.target.value)} placeholder="e.g. B2B SaaS, targeting enterprise..."
              className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2 text-white/90 text-xs focus:border-[#7C3AED]/40 focus:outline-none placeholder:text-white/20" />
          </div>
          <div className="space-y-2">
            <span className="text-[10px] uppercase tracking-widest text-white/30 font-bold">Target AI</span>
            <div className="flex flex-wrap gap-1.5">
              {AI_TARGETS.map(ai => (
                <button key={ai} onClick={() => setTargetAi(ai)}
                  className={`px-3 py-1.5 text-xs rounded-full border font-medium transition-all capitalize ${targetAi === ai ? 'bg-[#7C3AED] text-white border-[#7C3AED]' : 'bg-white/[0.03] text-white/40 border-white/[0.08] hover:text-white/60'}`}>{ai}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <span className="text-[10px] uppercase tracking-widest text-white/30 font-bold block mb-1">Max iterations</span>
              <select value={maxIter} onChange={e => setMaxIter(Number(e.target.value))}
                className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2 text-white text-xs focus:outline-none" data-testid="max-iter">
                {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div>
              <span className="text-[10px] uppercase tracking-widest text-white/30 font-bold block mb-1">Quality threshold</span>
              <select value={threshold} onChange={e => setThreshold(Number(e.target.value))}
                className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2 text-white text-xs focus:outline-none" data-testid="threshold">
                {[0.6, 0.7, 0.8, 0.9, 0.95].map(t => <option key={t} value={t}>{Math.round(t * 100)}%</option>)}
              </select>
            </div>
          </div>
          <Button onClick={handleLoop} disabled={loading || outcome.trim().length < 5}
            className="w-full shimmer-btn text-white rounded-full font-space font-bold border-0" data-testid="loop-btn">
            {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Refining...</> : <><RefreshCw className="w-4 h-4 mr-2" /> Start PI Loop</>}
          </Button>
          {loading && <p className="text-[11px] text-white/25 text-center">This may take 15&ndash;30 seconds...</p>}
        </div>

        {/* Result */}
        <div className="bg-[#0E1830] border border-white/5 rounded-2xl p-6">
          {result ? (
            <div className="space-y-4" data-testid="loop-result">
              {/* Iteration Progress */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold uppercase text-white/30 tracking-wider">Refinement Progress</span>
                  <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full ${result.threshold_met ? 'bg-emerald-500/10 text-emerald-400' : 'bg-amber-500/10 text-amber-400'}`} data-testid="threshold-badge">
                    {result.threshold_met ? 'Threshold met' : 'Below threshold'}
                  </span>
                </div>
                {result.iterations.map(it => (
                  <div key={it.iteration} className="flex items-center gap-3 bg-white/[0.03] rounded-lg p-2.5">
                    <span className="text-xs font-bold text-white/40 w-6">#{it.iteration}</span>
                    <div className="flex-1 h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${it.score >= threshold ? 'bg-emerald-500' : it.score >= 0.6 ? 'bg-amber-500' : 'bg-red-500'}`}
                        style={{ width: `${it.score * 100}%` }} />
                    </div>
                    <span className={`text-xs font-bold ${it.score >= threshold ? 'text-emerald-400' : 'text-amber-400'}`}>{Math.round(it.score * 100)}%</span>
                  </div>
                ))}
              </div>

              {/* Final Cast */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold text-emerald-400 uppercase tracking-wider">Final Cast &middot; Score {Math.round(result.final_score * 100)}%</span>
                  <div className="flex gap-2">
                    <button onClick={handleCopy} className="text-xs text-[#7C3AED] hover:underline flex items-center gap-1" data-testid="copy-loop">
                      {copied ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
                    </button>
                    <button onClick={handleSave} disabled={saving} className="text-xs text-emerald-400 hover:underline flex items-center gap-1" data-testid="save-loop">
                      <Save className="w-3 h-3" /> {saving ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </div>
                <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4 max-h-[250px] overflow-y-auto">
                  <pre className="text-white/80 text-sm leading-relaxed whitespace-pre-wrap font-mono m-0">{result.final_cast}</pre>
                </div>
                <p className="text-white/20 text-[10px] mt-2">{result.token_count} tokens &middot; {result.total_iterations} iterations &middot; {result.target_ai}</p>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center py-12">
              <RefreshCw className="w-10 h-10 text-white/10 mb-3" />
              <p className="text-white/30 text-sm">State your outcome to start the loop</p>
              <p className="text-white/15 text-xs mt-1">Generate, evaluate, refine &mdash; automatically</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
